import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { Profile, UserRole, UserStatus } from '@/types';
import { mockProfiles } from '@/mock/profiles';
import { getSupabase, isSupabaseConfigured } from '@/services/supabase';

interface AuthContextValue {
  user: Profile | null;
  loading: boolean;
  /**
   * Sign in with email + password. Returns either an error message OR the
   * just-signed-in profile so callers can route immediately based on role
   * without waiting for the React state update to propagate.
   */
  signIn: (email: string, password: string) => Promise<{ error?: string; profile?: Profile }>;
  signOut: () => void;
  /** Phase-1 helper to switch role inside the mock environment */
  switchToRole: (role: UserRole) => void;
  /** Whether the app is currently using mock auth (Phase 1) — for UI hints */
  isMockAuth: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const STORAGE_KEY = 'ln-mock-user-id';

const useMockAuth = (import.meta.env.VITE_USE_MOCK_AUTH ?? 'true') === 'true';

/**
 * Auto-logout after this many minutes of inactivity. We're handling
 * sensitive personal data (AHPRA, passports, vaccination records), so the
 * default is intentionally tight. Override per-deploy via env var if you
 * want a longer window for admin terminals.
 */
const IDLE_TIMEOUT_MIN = Number(import.meta.env.VITE_IDLE_TIMEOUT_MIN ?? 30);
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'mousedown', 'keydown', 'scroll', 'touchstart',
  // Note: 'visibilitychange' is a Document event (not Window) — left out
  // here. Click/key/scroll/touch already reset the timer when the tab
  // returns to focus.
];

// Postgres row → frontend Profile shape. Lives here so the rest of the app
// keeps consuming the camelCase Profile we already use everywhere.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function rowToProfile(row: any): Profile {
  return {
    id: row.id,
    authUserId: row.auth_user_id,
    email: row.email,
    firstName: row.first_name,
    middleName: row.middle_name ?? null,
    surname: row.surname,
    phone: row.phone ?? null,
    avatarUrl: row.avatar_url ?? null,
    role: row.role as UserRole,
    status: row.status as UserStatus,
    lastLoginAt: row.last_login_at ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function loadProfileForSession(session: Session): Promise<Profile | null> {
  const supa = getSupabase();
  const { data, error } = await supa
    .from('profiles')
    .select('id, auth_user_id, email, first_name, middle_name, surname, phone, avatar_url, role, status, last_login_at, created_at, updated_at')
    .eq('auth_user_id', session.user.id)
    .maybeSingle();
  if (error) {
    console.error('[auth] failed to load profile', error);
    return null;
  }
  if (!data) {
    // Auth user exists but no profile row — sign them out so they don't get
    // stuck on a half-bound session. Profile rows are seeded server-side
    // (admin invite flow); a missing one means setup is incomplete.
    console.error('[auth] no profile found for auth_user_id', session.user.id);
    return null;
  }
  return rowToProfile(data);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Initial session hydration + auth-state listener ────────────────
  useEffect(() => {
    // Mock-auth path: hydrate from localStorage, no Supabase calls
    if (useMockAuth) {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const found = mockProfiles.find((p) => p.id === stored) ?? null;
        setUser(found);
      }
      setLoading(false);
      return;
    }

    // Real-auth path: ask Supabase if there's a live session, then subscribe
    if (!isSupabaseConfigured) {
      console.error('[auth] VITE_USE_MOCK_AUTH=false but Supabase env vars are missing');
      setLoading(false);
      return;
    }

    const supa = getSupabase();
    let cancelled = false;

    // Initial session resolution — with a hard timeout so we don't hang
    // the whole app on a flaky Supabase call. If the timeout fires we
    // proceed as if there's no session; a real session will be picked up
    // by the auth-state listener as soon as the SDK recovers.
    (async () => {
      try {
        const sessionPromise = supa.auth.getSession();
        const timeout = new Promise<{ data: { session: null } }>((resolve) =>
          setTimeout(() => {
            console.warn('[auth] getSession() exceeded 5s — assuming no session');
            resolve({ data: { session: null } });
          }, 5000),
        );
        const { data: { session } } = await Promise.race([sessionPromise, timeout]);
        if (cancelled) return;
        if (session) {
          const profile = await loadProfileForSession(session);
          if (cancelled) return;
          setUser(profile);
          // Half-bound (auth user with no profile) → fire-and-forget signOut.
          // Awaiting can hang if the API is slow; the local state is what
          // gates the UI, so we don't need to wait for the server.
          if (!profile) void supa.auth.signOut();
        }
      } catch (e) {
        console.warn('[auth] initial session load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const { data: sub } = supa.auth.onAuthStateChange((event, session) => {
      // Run the async work outside the listener so the SDK doesn't await our
      // DB call (which could in theory deadlock against its own internal
      // state). Errors here must never propagate up.
      void (async () => {
        try {
          if (event === 'SIGNED_OUT' || !session) {
            setUser(null);
            return;
          }
          if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') {
            const profile = await loadProfileForSession(session);
            setUser(profile);
            if (!profile) void supa.auth.signOut();
          }
        } catch (e) {
          console.warn('[auth] auth-state-change handler failed', e);
        }
      })();
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // ── signIn ─────────────────────────────────────────────────────────
  const signIn = useCallback(async (email: string, password: string) => {
    if (useMockAuth) {
      const match =
        mockProfiles.find((p) => p.email.toLowerCase() === email.toLowerCase()) ??
        mockProfiles[0]!;
      localStorage.setItem(STORAGE_KEY, match.id);
      setUser(match);
      return { profile: match };
    }

    if (!isSupabaseConfigured) {
      return { error: 'Supabase is not configured. Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.' };
    }

    const supa = getSupabase();
    const { data, error } = await supa.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    if (!data.session) return { error: 'No session returned by Supabase.' };

    const profile = await loadProfileForSession(data.session);
    if (!profile) {
      await supa.auth.signOut();
      return { error: 'Account exists but no profile is configured. Contact an administrator.' };
    }
    setUser(profile);
    return { profile };
  }, []);

  // ── signOut ────────────────────────────────────────────────────────
  const signOut = useCallback(() => {
    if (useMockAuth) {
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
      return;
    }
    // Real Supabase signOut invalidates the JWT server-side too.
    void getSupabase().auth.signOut().finally(() => setUser(null));
  }, []);

  // ── Idle-timeout auto-logout ──────────────────────────────────────
  // Resets a timer on any activity. After IDLE_TIMEOUT_MIN of NO activity,
  // forces a sign-out and stores the current path so we can return to it
  // after the user re-authenticates.
  const idleTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!user) return; // Only track idle while signed in

    const reset = () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(() => {
        try {
          sessionStorage.setItem('ln-return-to', window.location.pathname + window.location.search);
        } catch { /* sessionStorage may be blocked in private mode — ignore */ }
        signOut();
      }, IDLE_TIMEOUT_MIN * 60_000);
    };

    reset();
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, reset, { passive: true });
    }
    return () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, reset);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, signOut]);

  // ── Mock-only role switcher ────────────────────────────────────────
  const switchToRole = useCallback((role: UserRole) => {
    if (!useMockAuth) return; // no-op outside mock
    const match = mockProfiles.find((p) => p.role === role);
    if (match) {
      localStorage.setItem(STORAGE_KEY, match.id);
      setUser(match);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, switchToRole, isMockAuth: useMockAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** Convenience role guards */
export const ADMIN_ROLES: UserRole[] = ['recruiter', 'compliance_admin', 'admin', 'super_admin'];
export function isAdmin(role: UserRole | undefined): boolean {
  return role ? ADMIN_ROLES.includes(role) : false;
}
export function isDoctor(role: UserRole | undefined): boolean {
  return role === 'doctor';
}
